import { computed } from 'vue'
import type { Feature } from 'geojson'
import type {
  GeoJSONSource,
  MapboxMap as Map,
} from '@/lib/map-x/webmap-viewer-types'
import split from '@/lib/split'
import { MAP_MAX_ZOOM, MAP_MIN_ZOOM, WAYPOINTS_SOURCE } from '@/lib/enums'
import { log } from '@/services/logging-service'
import { isActive } from '@/services/feature-flag-service'
import marketingEventService from '@/services/marketing-event-service'

const SPLIT_NAME = 'fox_waypoint_clustering'
export const WAYPOINT_CLUSTER_LAYER_ID = 'waypoint-clusters'
export const DEFAULT_PARAMS = {
  maxZoom: 10,
  minPoints: 2,
  radius: 50,
}

const VALID_PARAMS = {
  maxZoom: {
    min: MAP_MIN_ZOOM,
    max: MAP_MAX_ZOOM,
  },
  minPoints: {
    min: DEFAULT_PARAMS.minPoints,
    // There is no mapbox upper limit mentioned in SDK
    // but picking a what seems a reasonable number
    max: 999,
  },
  radius: {
    min: 0,
    // Radius is measured in pixel and relates to tile width
    // which is 512px in mapbox
    max: 512,
  },
}

type WaypointClusteringSplitParams = {
  config: {
    clusterMaxZoom?: number
    clusterMinPoints?: number
    clusterRadius?: number
  }
}

export const isWaypointClusteringEnabled = computed(() => isActive(SPLIT_NAME))

/**
 * Get the parameters for waypoints clustering
 * @returns - The  parameters for waypoints clustering
 */
export const getWaypointClusteringParams = () => {
  const { config } = split.getSplitConfigByKey(
    SPLIT_NAME
  ) as WaypointClusteringSplitParams
  // use default params if split config is not set
  if (!config) {
    return DEFAULT_PARAMS
  }

  // override default params with split config params if available
  const clusterParams = { ...DEFAULT_PARAMS }
  if (
    config?.clusterMaxZoom &&
    isValidParam('maxZoom', config.clusterMaxZoom)
  ) {
    clusterParams.maxZoom = config.clusterMaxZoom
  }
  if (
    config?.clusterMinPoints &&
    isValidParam('minPoints', config.clusterMinPoints)
  ) {
    clusterParams.minPoints = config.clusterMinPoints
  }
  if (config?.clusterRadius && isValidParam('radius', config.clusterRadius)) {
    clusterParams.radius = config.clusterRadius
  }
  return clusterParams
}

/**
 * Check if the parameter is valid and within the allowed range
 * @param paramName - parameter name
 * @param paramValue - parameter value
 * @returns - wether the parameter is valid or not
 */
const isValidParam = (
  paramName: keyof typeof VALID_PARAMS,
  paramValue: number
) => {
  return (
    paramName in VALID_PARAMS &&
    typeof paramValue === 'number' &&
    paramValue >= VALID_PARAMS[paramName].min &&
    paramValue <= VALID_PARAMS[paramName].max
  )
}

/**
 * Get the cluster icon text color for the app via yellowstone tokens
 * @returns - The cluster icon text color for the app
 */
export const getClusterIconColors = () => {
  const textColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ys-color-text-constant-primary')
    .substring(0, 7)

  return {
    clusterIconTextColor: textColor,
  }
}

/**
 * Handle the cluster click and zooms user in with retrieved zoom level
 * @param map - The map instance
 * @param event - The map click event
 */
export const handleClusterClick = (
  map: Map,
  clusterFeatures: Feature<GeoJSON.Point>[]
) => {
  const clusterId = clusterFeatures[0]?.properties?.cluster_id
  const wpSource = map.getSource(WAYPOINTS_SOURCE) as GeoJSONSource

  if (clusterId && wpSource) {
    // get zoom expansion level for the cluster
    wpSource.getClusterExpansionZoom(clusterId, (err, zoom) =>
      handleClusterZoom(
        err,
        zoom,
        map,
        clusterFeatures[0].geometry.coordinates as [number, number],
        clusterFeatures[0]?.properties?.point_count
      )
    )
  }
}

/**
 *
 * @param err - Error
 * @param zoom - Zoom level
 * @param map - Map instance
 * @param clusterFeaturesCoords - Cluster features coordinates
 * @param clusterPointCount - Cluster point count
 */
export const handleClusterZoom = (
  err: Error | null | undefined,
  zoom: number | null | undefined,
  map: Map,
  clusterFeaturesCoords: [number, number],
  clusterPointCount: number
) => {
  if (err || !zoom) {
    log('expand_waypoint_cluster_failed', {
      error: err,
      zoom: zoom,
    })
    return
  }
  // ease to the cluster center with new zoom
  map.easeTo({
    center: clusterFeaturesCoords,
    zoom: zoom,
  })

  // send marketing event for cluster click
  marketingEventService.publish('waypoint_cluster_click', {
    zoom_level: zoom,
    cluster_count: clusterPointCount,
  })
}
